import { useState, useEffect } from 'react';
import { get, post, put, del } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const GalleryEditor = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [currentImage, setCurrentImage] = useState({
    id: null,
    image_path: '',
    thumbnail_path: '',
    category: 'misc',
    display_order: 0,
    is_active: true,
    translations: {
      en: { title: '', alt_text: '' },
      cs: { title: '', alt_text: '' },
    },
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedThumbnail, setSelectedThumbnail] = useState(null);
  const language = getUserLanguage();

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('[GalleryEditor] Fetching gallery images');
      const headers = { 'accept-language': language };
      const data = await get('/gallery', headers);
      console.log('[GalleryEditor] Fetched data:', data);
      const processedData = (data || []).map(image => ({
        ...image,
        is_active: !!image.is_active,
        display_order: image.display_order || 0,
        translations: image.translations || {
          en: { title: '', alt_text: '' },
          cs: { title: '', alt_text: '' },
        },
      }));
      setGalleryImages(processedData);
    } catch (err) {
      console.error('[GalleryEditor] Error fetching gallery images:', err.message);
      setError(language === 'cs' ? 'Nepodařilo se načíst obrázky galerie' : 'Failed to load gallery images');
      setGalleryImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e, lang = language) => {
    const { name, value, type, checked } = e.target;
    console.log('[GalleryEditor] Input change:', { name, value, lang });
    if (['title', 'alt_text'].includes(name)) {
      setCurrentImage(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            ...prev.translations[lang],
            [name]: value,
          },
        },
      }));
    } else {
      setCurrentImage(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log('[GalleryEditor] Image selected:', file.name, file.size, 'bytes');
      setSelectedImage(file);
    }
  };

  const handleThumbnailChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      console.log('[GalleryEditor] Thumbnail selected:', file.name, file.size, 'bytes');
      setSelectedThumbnail(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      console.log('[GalleryEditor] Submitting form, editMode:', editMode);
      const formData = new FormData();

      if (selectedImage) {
        console.log('[GalleryEditor] Adding image to FormData:', selectedImage.name);
        formData.append('image', selectedImage);
      } else if (editMode && currentImage.image_path) {
        console.log('[GalleryEditor] Adding existing image_path:', currentImage.image_path);
        formData.append('image_path', currentImage.image_path);
      }

      if (selectedThumbnail) {
        console.log('[GalleryEditor] Adding thumbnail to FormData:', selectedThumbnail.name);
        formData.append('thumbnail', selectedThumbnail);
      } else if (editMode && currentImage.thumbnail_path) {
        console.log('[GalleryEditor] Adding existing thumbnail_path:', currentImage.thumbnail_path);
        formData.append('thumbnail_path', currentImage.thumbnail_path);
      }

      console.log('[GalleryEditor] Adding category:', currentImage.category || 'misc');
      formData.append('category', currentImage.category || 'misc');
      console.log('[GalleryEditor] Adding display_order:', currentImage.display_order || 0);
      formData.append('display_order', currentImage.display_order || 0);
      console.log('[GalleryEditor] Adding is_active:', currentImage.is_active ? 'true' : 'false');
      formData.append('is_active', currentImage.is_active ? 'true' : 'false');

      const translationsString = JSON.stringify(currentImage.translations);
      console.log('[GalleryEditor] Adding translations:', translationsString);
      formData.append('translations', translationsString);

      console.log('[GalleryEditor] FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`[GalleryEditor] ${key}:`, value instanceof File ? `${value.name} (${value.size} bytes)` : value);
      }

      let response;
      if (editMode) {
        console.log('[GalleryEditor] Sending PUT request to:', `/gallery/${currentImage.id}`);
        response = await put(`/gallery/${currentImage.id}`, formData);
        console.log('[GalleryEditor] PUT response:', response);
        setSuccess(language === 'cs' ? 'Obrázek galerie úspěšně aktualizován' : 'Gallery image updated successfully');
      } else {
        console.log('[GalleryEditor] Sending POST request to:', '/gallery');
        response = await post('/gallery', formData);
        console.log('[GalleryEditor] POST response:', response);
        setSuccess(language === 'cs' ? 'Obrázek galerie úspěšně přidán' : 'Gallery image added successfully');
      }

      resetForm();
      fetchGalleryImages();
    } catch (err) {
      console.error('[GalleryEditor] Error saving gallery image:', err.message);
      setError(language === 'cs' ? `Nepodařilo se uložit obrázek galerie: ${err.message}` : `Failed to save gallery image: ${err.message}`);
    }
  };

  const handleEdit = async (id) => {
    try {
      console.log('[GalleryEditor] Fetching image for edit, ID:', id);
      const image = await get(`/gallery/${id}`);
      console.log('[GalleryEditor] Fetched image:', image);
      setCurrentImage({
        id: image.id,
        image_path: image.image_path || '',
        thumbnail_path: image.thumbnail_path || '',
        category: image.category || 'misc',
        display_order: image.display_order || 0,
        is_active: !!image.is_active,
        translations: image.translations || {
          en: { title: '', alt_text: '' },
          cs: { title: '', alt_text: '' },
        },
      });
      setEditMode(true);
      setSelectedImage(null);
      setSelectedThumbnail(null);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('[GalleryEditor] Error fetching gallery image for edit:', err.message);
      setError(language === 'cs' ? 'Nepodařilo se načíst obrázek galerie pro úpravu' : 'Failed to load gallery image for editing');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tento obrázek galerie?' : 'Are you sure you want to delete this gallery image?')) {
      return;
    }
    try {
      console.log('[GalleryEditor] Deleting image, ID:', id);
      await del(`/gallery/${id}`);
      console.log('[GalleryEditor] Image deleted successfully');
      setSuccess(language === 'cs' ? 'Obrázek galerie úspěšně smazán' : 'Gallery image deleted successfully');
      fetchGalleryImages();
    } catch (err) {
      console.error('[GalleryEditor] Error deleting gallery image:', err.message);
      setError(language === 'cs' ? 'Nepodařilo se smazat obrázek galerie' : 'Failed to delete gallery image');
    }
  };

  const resetForm = () => {
    console.log('[GalleryEditor] Resetting form');
    setCurrentImage({
      id: null,
      image_path: '',
      thumbnail_path: '',
      category: 'misc',
      display_order: 0,
      is_active: true,
      translations: {
        en: { title: '', alt_text: '' },
        cs: { title: '', alt_text: '' },
      },
    });
    setSelectedImage(null);
    setSelectedThumbnail(null);
    setEditMode(false);
    setShowForm(false);
    setError(null);
    setSuccess(null);
  };

  const handleAddNew = () => {
    console.log('[GalleryEditor] Opening form for new image');
    resetForm();
    setShowForm(true);
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">{language === 'cs' ? 'Správa galerie' : 'Gallery Management'}</h1>
        <button onClick={handleAddNew} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Přidat obrázek' : 'Add Image'}
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && <div className="admin-alert admin-alert-success">{success}</div>}

      {showForm && (
        <div className="admin-card admin-mb-8 admin-bg-dark-gray">
          <div className="admin-card-header admin-bg-accent-gray">
            <h2 className="admin-text-xl admin-font-semibold">
              {editMode ? (language === 'cs' ? 'Upravit obrázek' : 'Edit Image') : (language === 'cs' ? 'Přidat obrázek' : 'Add Image')}
            </h2>
          </div>
          <div className="admin-card-body">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6">
                <div>
                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">English</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Title</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentImage.translations.en.title || ''}
                      onChange={(e) => handleChange(e, 'en')}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Alt Text</label>
                    <input
                      type="text"
                      name="alt_text"
                      className="admin-form-input"
                      value={currentImage.translations.en.alt_text || ''}
                      onChange={(e) => handleChange(e, 'en')}
                    />
                  </div>

                  <h3 className="admin-text-lg admin-font-medium admin-mb-2">Čeština</h3>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Název</label>
                    <input
                      type="text"
                      name="title"
                      className="admin-form-input"
                      value={currentImage.translations.cs.title || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                    />
                  </div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">Alternativní text</label>
                    <input
                      type="text"
                      name="alt_text"
                      className="admin-form-input"
                      value={currentImage.translations.cs.alt_text || ''}
                      onChange={(e) => handleChange(e, 'cs')}
                    />
                  </div>
                </div>

                <div>
                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Hlavní obrázek' : 'Main Image'}</label>
                    <input
                      type="file"
                      name="image"
                      className="admin-form-input"
                      onChange={handleImageChange}
                      accept="image/*"
                    />
                    {editMode && !selectedImage && currentImage.image_path && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální obrázek:' : 'Current image:'}</p>
                        <img
                          src={`${IMAGE_BASE_URL}${currentImage.image_path}`}
                          alt={currentImage.translations[language]?.title || 'Gallery Image'}
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                          onError={(e) => {
                            console.warn('[GalleryEditor] Image load failed:', currentImage.image_path);
                            e.target.onerror = null;
                            e.target.src = '/images/gallery/placeholder.jpg';
                          }}
                        />
                      </div>
                    )}
                    {selectedImage && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybraný obrázek:' : 'Selected image:'} {selectedImage.name}</p>
                        <img
                          src={URL.createObjectURL(selectedImage)}
                          alt="Preview"
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                        />
                      </div>
                    )}
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Miniatura' : 'Thumbnail'}</label>
                    <input
                      type="file"
                      name="thumbnail"
                      className="admin-form-input"
                      onChange={handleThumbnailChange}
                      accept="image/*"
                    />
                    {editMode && !selectedThumbnail && currentImage.thumbnail_path && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Aktuální miniatura:' : 'Current thumbnail:'}</p>
                        <img
                          src={`${IMAGE_BASE_URL}${currentImage.thumbnail_path}`}
                          alt={currentImage.translations[language]?.title || 'Gallery Thumbnail'}
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                          onError={(e) => {
                            console.warn('[GalleryEditor] Thumbnail load failed:', currentImage.thumbnail_path);
                            e.target.onerror = null;
                            e.target.src = '/images/gallery/placeholder.jpg';
                          }}
                        />
                      </div>
                    )}
                    {selectedThumbnail && (
                      <div className="admin-mt-2">
                        <p className="admin-text-sm admin-text-gray-300">{language === 'cs' ? 'Vybraná miniatura:' : 'Selected thumbnail:'} {selectedThumbnail.name}</p>
                        <img
                          src={URL.createObjectURL(selectedThumbnail)}
                          alt="Preview"
                          className="admin-mt-2 admin-h-32 admin-object-cover admin-rounded"
                        />
                      </div>
                    )}
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Kategorie' : 'Category'}</label>
                    <select
                      name="category"
                      className="admin-form-select"
                      value={currentImage.category || 'misc'}
                      onChange={handleChange}
                    >
                      <option value="team">{language === 'cs' ? 'Tým' : 'Team'}</option>
                      <option value="cars">{language === 'cs' ? 'Auta' : 'Cars'}</option>
                      <option value="events">{language === 'cs' ? 'Události' : 'Events'}</option>
                      <option value="misc">{language === 'cs' ? 'Ostatní' : 'Miscellaneous'}</option>
                    </select>
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-form-label">{language === 'cs' ? 'Pořadí zobrazení' : 'Display Order'}</label>
                    <input
                      type="number"
                      name="display_order"
                      className="admin-form-input"
                      value={currentImage.display_order || 0}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>

                  <div className="admin-mb-4">
                    <label className="admin-flex admin-items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        className="admin-form-checkbox"
                        checked={!!currentImage.is_active}
                        onChange={handleChange}
                      />
                      <span className="admin-text-white admin-text-sm admin-font-bold">{language === 'cs' ? 'Aktivní' : 'Active'}</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="admin-mt-6 admin-flex admin-items-center">
                <button type="submit" className="admin-btn admin-btn-primary admin-mr-2">
                  {editMode ? (language === 'cs' ? 'Aktualizovat' : 'Update') : (language === 'cs' ? 'Přidat' : 'Add')}
                </button>
                <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
                  {language === 'cs' ? 'Zrušit' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">{language === 'cs' ? 'Seznam obrázků v galerii' : 'Gallery Images List'}</div>
        {galleryImages.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">{language === 'cs' ? 'Žádné obrázky nebyly nalezeny.' : 'No images found.'}</div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Název' : 'Title'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Kategorie' : 'Category'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Pořadí' : 'Order'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Aktivní' : 'Active'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {galleryImages.map((image) => {
                  const imageData = image.translations && image.translations[language]
                    ? image.translations[language]
                    : (image.translations && image.translations.en
                      ? image.translations.en
                      : { title: '', alt_text: '' });
                  return (
                    <tr key={image.id} className="admin-table-row">
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-flex admin-items-center">
                          <div className="admin-flex-shrink-0 admin-h-10 admin-w-10">
                            <img
                              className="admin-h-10 admin-w-10 admin-rounded-full admin-object-cover"
                              src={image.thumbnail_path ? `${IMAGE_BASE_URL}${image.thumbnail_path}` : `${IMAGE_BASE_URL}${image.image_path}`}
                              alt={imageData.title || 'Gallery Image'}
                              onError={(e) => {
                                console.warn('[GalleryEditor] Table image load failed:', image.thumbnail_path || image.image_path);
                                e.target.onerror = null;
                                e.target.src = '/images/gallery/placeholder.jpg';
                              }}
                            />
                          </div>
                          <div className="admin-ml-4">
                            <div className="admin-text-sm admin-font-medium admin-text-white">
                              {imageData.title || (language === 'cs' ? 'Bezejmenný obrázek' : 'Unnamed Image')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">
                          {image.category === 'team' ? (language === 'cs' ? 'Tým' : 'Team') :
                           image.category === 'cars' ? (language === 'cs' ? 'Auta' : 'Cars') :
                           image.category === 'events' ? (language === 'cs' ? 'Události' : 'Events') :
                           (language === 'cs' ? 'Ostatní' : 'Miscellaneous')}
                        </div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <div className="admin-text-sm admin-text-white">{image.display_order || 0}</div>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                        <span className={`admin-badge ${image.is_active ? 'admin-badge-green' : 'admin-badge-red'}`}>
                          {image.is_active ? (language === 'cs' ? 'Ano' : 'Yes') : (language === 'cs' ? 'Ne' : 'No')}
                        </span>
                      </td>
                      <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                        <button onClick={() => handleEdit(image.id)} className="admin-text-blue-600 hover:admin-text-blue-900 admin-mr-3">
                          {language === 'cs' ? 'Upravit' : 'Edit'}
                        </button>
                        <button onClick={() => handleDelete(image.id)} className="admin-text-red-600 hover:admin-text-red-900">
                          {language === 'cs' ? 'Smazat' : 'Delete'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryEditor;